import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HTTPCONSTANTS } from '../http/http-constants';
import {
  DossierResult,
  EditorialIntelligenceDossier,
  MediaDiscoveryResult,
} from '../models/editorial/editorialIntelligence';
import {
  DEFAULT_REGISTRATION,
  MOCK_DOSSIERS,
} from '../pages/admin-editorial-intelligence/mock/editorial-intelligence.mock';

/**
 * Data source for the admin Editorial Intelligence view.
 *
 * LIVE against the isolated Dev2 Plato FastAPI/Lambda HttpApi
 * (PlatoAPILambda-dev2), which serves the rich computed intelligence dossier at
 * GET /admin/editorial/registration/{reg} in exactly the DossierResult shape.
 * The base URL comes from environment.editorialApiBaseAddress via the dedicated
 * editorial placeholder (UrlInterceptor) — NOT the .NET api-dev2 host.
 *
 * Resilient by design: any API failure (network / 4xx / 5xx / empty body)
 * transparently falls back to the bundled MOCK dossier when one exists, and
 * reports `source: 'mock'`, so the admin view never hard-fails. Set
 * USE_LIVE_API = false to force mock-only.
 */
@Injectable({ providedIn: 'root' })
export class EditorialIntelligenceService {
  // Live Dev2 Plato HttpApi serves the rich dossier; mock remains the fallback.
  private static readonly USE_LIVE_API = true;

  constructor(private http: HttpClient) {}

  public get defaultRegistration(): string {
    return DEFAULT_REGISTRATION;
  }

  /** Normalised, e.g. 'fu 2' -> 'FU2' (matches the backend normalisation). */
  public normalize(registration: string): string {
    return (registration || '').toUpperCase().replace(/\s+/g, '').trim();
  }

  /** Registrations available in the local mock (for pickers / fallbacks). */
  public availableRegistrations(): string[] {
    return Object.keys(MOCK_DOSSIERS);
  }

  /**
   * Fetch a dossier and tell the caller whether it came from the API or mock.
   * Never throws: an API failure transparently falls back to mock if present.
   */
  public getDossier(registration: string): Observable<DossierResult | null> {
    const reg = this.normalize(registration);
    const mock = MOCK_DOSSIERS[reg] || null;

    if (!EditorialIntelligenceService.USE_LIVE_API) {
      return of(mock ? { dossier: mock, source: 'mock' as const } : null);
    }

    const url = `${HTTPCONSTANTS.editorialApiProtocol}${HTTPCONSTANTS.editorialApiBaseAddress}/admin/editorial/registration/${encodeURIComponent(
      reg
    )}`;
    return this.http.get<any>(url).pipe(
      map((res) => {
        const dossier = this.mapApiResponse(res);
        return dossier ? { dossier, source: 'api' as const } : mockResult(mock);
      }),
      catchError(() => of(mockResult(mock)))
    );
  }

  private editorialBase(): string {
    return `${HTTPCONSTANTS.editorialApiProtocol}${HTTPCONSTANTS.editorialApiBaseAddress}`;
  }

  /**
   * Run the Media Discovery stage for a registration (POST). This makes external
   * no-key provider calls server-side and persists candidates for review; the
   * caller should re-fetch the dossier afterwards to see the new media.
   * Never throws: a failure resolves to a synthetic 'egress_unavailable'-style
   * result so the UI can show a message instead of hard-failing.
   */
  public discoverMedia(registration: string): Observable<MediaDiscoveryResult> {
    const reg = this.normalize(registration);
    if (!EditorialIntelligenceService.USE_LIVE_API) {
      return of({
        status: 'egress_unavailable',
        detail: 'Live API disabled (mock mode); media discovery is unavailable.',
      } as MediaDiscoveryResult);
    }
    const url = `${this.editorialBase()}/admin/editorial/registration/${encodeURIComponent(
      reg
    )}/discover-media`;
    return this.http.post<MediaDiscoveryResult>(url, {}).pipe(
      catchError(() =>
        of({
          status: 'egress_unavailable',
          detail:
            'Media discovery request failed (network or server error). No changes were made.',
        } as MediaDiscoveryResult)
      )
    );
  }

  /**
   * Build (or rebuild) the Editorial Evidence Dossier for a registration (M1).
   * POST /build-dossier. Deterministic, read-only against prod plate data; writes
   * only the Dev2 editorial DB. `discover` optionally runs media discovery inline
   * (kept false here — the workbench triggers discovery as a separate step).
   * Never throws: failures resolve to a synthetic error result.
   */
  public buildDossier(
    registration: string,
    discover = false
  ): Observable<any> {
    const reg = this.normalize(registration);
    if (!EditorialIntelligenceService.USE_LIVE_API) {
      return of({ status: 'error', detail: 'Live API disabled (mock mode).' });
    }
    const url = `${this.editorialBase()}/admin/editorial/registration/${encodeURIComponent(
      reg
    )}/build-dossier?discover=${discover ? 'true' : 'false'}`;
    return this.http.post<any>(url, {}).pipe(
      catchError(() =>
        of({ status: 'error', detail: 'Build failed (network or server error). No changes were made.' })
      )
    );
  }

  /**
   * Import editor-pasted reference URLs. POST /import-references. Returns the
   * per-URL results (added / deduped / failed + rights/title/source/thumbnail),
   * or a synthetic error result. Never throws.
   */
  public importReferences(
    registration: string,
    urls: string[],
    notes?: string,
    suggestedUse?: string
  ): Observable<any> {
    const reg = this.normalize(registration);
    if (!EditorialIntelligenceService.USE_LIVE_API) {
      return of({ status: 'error', detail: 'Live API disabled (mock mode).' });
    }
    const url = `${this.editorialBase()}/admin/editorial/registration/${encodeURIComponent(
      reg
    )}/import-references`;
    return this.http.post<any>(url, {
      urls, notes: notes ?? null, suggestedUse: suggestedUse ?? 'unknown',
    }).pipe(catchError(() => of({ status: 'error', detail: 'Import failed (network or server error).' })));
  }

  /**
   * List articles for the review queue. GET /admin/editorial(?status=). Returns
   * [] on failure so the queue never hard-fails.
   */
  public listArticles(status?: string): Observable<any[]> {
    if (!EditorialIntelligenceService.USE_LIVE_API) return of([]);
    const q = status && status !== 'all' ? `?status=${encodeURIComponent(status)}` : '';
    const url = `${this.editorialBase()}/admin/editorial${q}`;
    return this.http.get<any[]>(url).pipe(catchError(() => of([])));
  }

  /**
   * Article-level review action. action ∈ approve | reject | request-changes.
   * POST /admin/editorial/articles/{id}/{action}. Returns null on failure.
   * (Not publishing — this only moves the review status.)
   */
  public articleAction(
    id: number,
    action: 'approve' | 'reject' | 'request-changes',
    notes?: string
  ): Observable<any | null> {
    if (!EditorialIntelligenceService.USE_LIVE_API) return of(null);
    const url = `${this.editorialBase()}/admin/editorial/articles/${id}/${action}`;
    return this.http.post<any>(url, { notes: notes ?? null }).pipe(catchError(() => of(null)));
  }

  /**
   * Save an editor review note WITHOUT changing status.
   * POST /admin/editorial/articles/{id}/note. Returns null on failure.
   */
  public saveArticleNotes(id: number, notes: string): Observable<any | null> {
    if (!EditorialIntelligenceService.USE_LIVE_API) return of(null);
    const url = `${this.editorialBase()}/admin/editorial/articles/${id}/note`;
    return this.http.post<any>(url, { notes: notes ?? null }).pipe(catchError(() => of(null)));
  }

  /**
   * Fetch the Story Detection + Editorial Brief (M2). GET /story-brief. Returns
   * null on failure (e.g. no dossier yet — build it first).
   */
  public getStoryBrief(registration: string): Observable<any | null> {
    const reg = this.normalize(registration);
    if (!EditorialIntelligenceService.USE_LIVE_API) return of(null);
    const url = `${this.editorialBase()}/admin/editorial/registration/${encodeURIComponent(
      reg
    )}/story-brief`;
    return this.http.get<any>(url).pipe(catchError(() => of(null)));
  }

  /**
   * Generate + persist the deterministic article (M3). POST /generate-article.
   * Returns the article, or a not_ready/error result. Never throws.
   */
  public generateArticle(registration: string): Observable<any> {
    const reg = this.normalize(registration);
    if (!EditorialIntelligenceService.USE_LIVE_API) {
      return of({ status: 'error', detail: 'Live API disabled (mock mode).' });
    }
    const url = `${this.editorialBase()}/admin/editorial/registration/${encodeURIComponent(
      reg
    )}/generate-article`;
    return this.http.post<any>(url, {}).pipe(
      catchError(() => of({ status: 'error', detail: 'Article generation failed (network or server error).' }))
    );
  }

  /**
   * Apply an editor action to a single discovered media asset.
   * action ∈ approve | reject | flag | note. Returns true on success.
   */
  public mediaAction(
    mediaId: number | string,
    action: 'approve' | 'reject' | 'flag' | 'note',
    notes?: string
  ): Observable<boolean> {
    if (!EditorialIntelligenceService.USE_LIVE_API) {
      return of(false);
    }
    const url = `${this.editorialBase()}/admin/editorial/media/${encodeURIComponent(
      String(mediaId)
    )}/${action}`;
    return this.http.post<any>(url, { notes: notes ?? null }).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  /**
   * Adapt the API payload to the dossier contract. The Dev2 endpoint is being
   * built to return this shape directly, so this is currently a passthrough;
   * add field mapping here if the API wraps or renames anything.
   */
  private mapApiResponse(res: any): EditorialIntelligenceDossier | null {
    if (!res) return null;
    return (res.dossier || res) as EditorialIntelligenceDossier;
  }
}

function mockResult(
  mock: EditorialIntelligenceDossier | null
): DossierResult | null {
  return mock ? { dossier: mock, source: 'mock' } : null;
}
